import React, {useMemo, useState} from 'react';
import {Alert, FlexRow, Modal, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {ExpenseRow} from '../ExpenseTypes';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import BulkUpdateExpenseItemsMutation from '../../../mutations/bulk_update_expense_items_mutation';
import Util from '../../../forecast-app/shared/util/util';
import {partitionArray} from '../../../forecast-app/shared/util/array-utils';
import {FieldLabel, PlaceholderItem} from './ModalComponents';
import ConditionalDropdown from '../../dropdowns/conditional-dropdown/ConditionalDropdown';
import {getNotNullishNodes} from '../../../forecast-app/shared/util/NotNullPredicate';
import {createFragmentContainer, graphql} from 'react-relay';
import {ExpenseItemChangePersonModal_viewer} from './__generated__/ExpenseItemChangePersonModal_viewer.graphql';

type Person = NonNullable<
	NonNullable<NonNullable<NonNullable<ExpenseItemChangePersonModal_viewer['company']>['allPersons']>['edges']>[0]
>['node'];

type ExpenseManagementModalProps = {
	selectedRows: ExpenseRow[];
	closeModal: () => void;
	viewer: ExpenseItemChangePersonModal_viewer;
};

const PersonOption = (props: {person: Person}) => {
	const {fullName} = props.person || {};
	return (
		<FlexRow justifyContent={'flex-start'}>
			<Text size={'2'} noWrap>
				{fullName || ''}
			</Text>
		</FlexRow>
	);
};

const findCommonPerson = (expenseItems: ExpenseRow[]) => {
	if (expenseItems.length === 0) {
		return null;
	}
	const firstPersonId = expenseItems[0].person?.id;

	return expenseItems.every(({person}) => person?.id === firstPersonId) ? expenseItems[0].person : null;
};

const ExpenseItemChangePersonModal = ({selectedRows, closeModal, viewer}: ExpenseManagementModalProps): JSX.Element => {
	const {formatMessage} = useIntl();

	const [selectedLockedRows, selectedUnlockedRows] = useMemo(
		() => partitionArray(selectedRows, item => item.locked),
		[selectedRows]
	);

	const possiblePersons: Person[] = useMemo(() => getNotNullishNodes(viewer.company?.allPersons?.edges), [viewer]);

	const commonPerson = useMemo(() => findCommonPerson(selectedUnlockedRows), [selectedUnlockedRows]);

	const [selectedPerson, setSelectedPerson] = useState(commonPerson);

	const numLockedExpenses = selectedLockedRows.length;
	const numTotalExpenses = selectedRows.length;

	const onUpdate = () => {
		closeModal();
		if (selectedPerson === null || selectedPerson?.id === commonPerson?.id) {
			return;
		}
		const onSuccess = () => {
			createToast({
				duration: 5000,
				message: formatMessage({
					id:
						selectedUnlockedRows.length > 1
							? 'expense_management.has_been_updated_bulk'
							: 'expense_management.has_been_updated',
				}),
			});
		};
		Util.CommitMutation(
			BulkUpdateExpenseItemsMutation,
			{
				ids: selectedUnlockedRows.map(({id}) => id),
				personId: selectedPerson?.id,
			},
			onSuccess
		);
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="expense_management.modal.change_person.change_person" />
			</Modal.Title>
			<Modal.Content>
				<FieldLabel
					helperTextId="expense_management.modal.change_person.assign_person_all_selected"
					labelId="expense_management.modal.change_person.person"
				>
					<ConditionalDropdown
						options={possiblePersons}
						useTriggerWidth
						autoSelect={false}
						renderOption={person => <PersonOption person={person} />}
						onSelect={person => person && setSelectedPerson(person)}
						data-cy={'select-person'}
					>
						{selectedPerson ? (
							<PersonOption person={selectedPerson} />
						) : (
							<PlaceholderItem messageId={'expense_management.modal.change_person.assign_person'} />
						)}
					</ConditionalDropdown>
				</FieldLabel>
				{selectedLockedRows.length > 0 && (
					<Alert
						text={formatMessage(
							{id: 'expense_management.modal.change_person.locked_alert'},
							{numLockedExpenses, numTotalExpenses}
						)}
						type={Alert.TYPE.INFORMATION}
					/>
				)}
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={onUpdate}>
					<FormattedMessage id="expense_management.actions.confirm_changes" />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const ExpenseItemChangePersonModalQuery = graphql`
	query ExpenseItemChangePersonModalQuery {
		viewer {
			actualPersonId
			component(name: "ExpenseItemChangePersonModal")
			...ExpenseItemChangePersonModal_viewer
		}
	}
`;

export default createFragmentContainer(ExpenseItemChangePersonModal, {
	viewer: graphql`
		fragment ExpenseItemChangePersonModal_viewer on Viewer {
			company {
				allPersons(first: 10000, excludeClientUsers: true) @connection(key: "Company_allPersons", filters: []) {
					edges {
						node {
							id
							fullName
							profilePictureId
						}
					}
				}
			}
		}
	`,
});
